import { ReactNode } from "react";

import {
  DashboardIcon,
  DotIcon,
  GeohashIcon,
  H3Icon,
  LineIcon,
  LockIcon,
  LookupIcon,
  MakerIcon,
  MapIcon,
  PremiumIcon
} from "@/assets/icons";
import Badge from "@/components/ui/badge";
import { Divider } from "@/components/ui/Divider";
import { COUNTRY_NAME } from "@/constants/dataset";
import { DatasetExploreItem } from "@/types/dataset";
import { LayerType } from "@/types/map";

import { twMerge } from "tailwind-merge";

type SpatialInfo = {
  [key: string]: {
    icon: ReactNode;
    label: string;
    textColor: string;
  };
};

type Props = {
  data: DatasetExploreItem;
  isSelected: boolean;
  isUseNewDatasetExplorerModal?: boolean;
  handleSelectData: (data: DatasetExploreItem) => void;
};

export const SPATIAL_INFO: SpatialInfo = {
  [LayerType.ADMINISTRATIVE]: {
    icon: <MapIcon className="fill-royal-blue w-[12px]" />,
    label: "Admin. Area",
    textColor: "text-royal-blue"
  },
  [LayerType.POLYGON]: {
    icon: <DashboardIcon className="fill-tangerine w-[12px]" />,
    label: "Polygon",
    textColor: "text-tangerine"
  },
  [LayerType.POINT]: {
    icon: <MakerIcon className="fill-blue-violet w-[12px]" />,
    label: "Point",
    textColor: "text-blue-violet"
  },
  [LayerType.GRID]: {
    icon: <LookupIcon className="fill-teal-500 size-3" />,
    label: "Grid",
    textColor: "text-blue-violet"
  },
  [LayerType.PROFILING]: {
    icon: <LookupIcon className="fill-teal-500 size-3" />,
    label: "Profiling",
    textColor: "text-blue-violet"
  },
  [LayerType.H3]: {
    icon: <H3Icon className="fill-teal-500 size-3" />,
    label: "H3",
    textColor: "text-teal-500"
  },
  [LayerType.GEOHASH]: {
    icon: <GeohashIcon className="size-3" />,
    label: "Geohash",
    textColor: "text-dim-gray"
  },
  [LayerType.LINE]: {
    icon: <LineIcon className="size-3 fill-purple-300" />,
    label: "Line",
    textColor: "text-dim-gray"
  }
};

const DatasetCard = ({
  data,
  isSelected,
  isUseNewDatasetExplorerModal,
  handleSelectData
}: Props) => {
  const { label, icon, textColor } =
    SPATIAL_INFO[data.spatial_aggregation_type] ||
    SPATIAL_INFO[LayerType.POLYGON];

  const isPremium = isUseNewDatasetExplorerModal && data?.license === "premium";
  const isLocked = isUseNewDatasetExplorerModal && !data.is_subscribed;

  return (
    <div
      className={twMerge(
        "flex flex-col font-sans border-[1px] rounded-lg p-4 gap-2 min-w-[266px] min-h-[114px] h-full cursor-pointer justify-between relative",
        isSelected ? "bg-teal-50 border-teal-200" : "bg-white border-gainsboro"
      )}
      role="button"
      onClick={() => handleSelectData(data)}
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && handleSelectData(data)}
    >
      <div className="flex gap-2 items-center">
        {isUseNewDatasetExplorerModal && (
          <div className="h-10 w-10 border rounded-lg flex items-center justify-center">
            {data?.provider?.logo_asset_url ? (
              <img
                src={data?.provider?.logo_asset_url}
                alt={`logo-${data.source}`}
                className="h-8 w-8"
              />
            ) : null}
          </div>
        )}
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <p
            className={twMerge(
              "text-sm text-txtHeading font-bold truncate",
              isSelected && "text-teal-500",
              isLocked ? "pr-7" : ""
            )}
            title={data.name}
          >
            {data.name}
          </p>
          <p className="text-[10px] truncate" title={data.short_description}>
            {data.short_description}
          </p>
        </div>
      </div>
      {/* Location place info */}
      <div className="flex flex-col gap-1">
        <Divider className="bg-gainsboro w-[100%] h-[1px]" />
        <div className="flex justify-between mt-1">
          <div className="flex items-center gap-1">
            {icon}
            <span className={twMerge("text-[10px]", textColor)}>{label}</span>
            <div className="flex items-center gap-1 px-2">
              <DotIcon className="fill-light-gray" />
              <span className="text-[10px] text-grey">
                {COUNTRY_NAME[data?.country_id || "id"]}
              </span>
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <span className="text-[10px] text-grey">{data.provider?.name}</span>
            {isPremium && (
              <Badge className="bg-solid-green border-solid-green rounded-[4px] flex items-center justify-center">
                <PremiumIcon
                  className="fill-white"
                  aria-label="Premium dataset"
                  title="Premium dataset"
                />
              </Badge>
            )}
          </div>
        </div>
      </div>
      {/* Lock Icon */}
      {isLocked && (
        <Badge className="absolute top-0 right-4 bg-[#E37900] border-[#E37900] rounded-tr-none rounded-tl-none p-1">
          <LockIcon
            className="fill-white"
            aria-label="Locked dataset"
            title="Locked dataset"
          />
        </Badge>
      )}
    </div>
  );
};
export default DatasetCard;
